import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { Save } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase";
import { sanitizeForFirestore } from "../../lib/firebaseSafe";
import { ADMIN_EMAILS } from "../../lib/platformData";
import { DEFAULT_LEGAL_MENTIONS, mergeLegalMentions } from "../LegalMentions";

function splitBlocks(value) {
  return String(value || "")
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function Field({ label, value, onChange, textarea = false }) {
  const Input = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <Input
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full rounded-lg border border-white/10 bg-black px-3 py-2 text-white outline-none transition focus:border-red-500 ${
          textarea ? "min-h-[140px]" : ""
        }`}
      />
    </label>
  );
}

export default function AdminLegalMentions() {
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const toast = useToast();
  const [legal, setLegal] = useState(() => mergeLegalMentions());
  const [saving, setSaving] = useState(false);
  const isAdmin = Boolean(user && ADMIN_EMAILS.includes(user.email));

  useEffect(() => {
    if (!isAdmin) return undefined;
    return onSnapshot(doc(db, "admin", "content"), (snap) => {
      const data = snap.exists() ? snap.data() : {};
      setLegal(mergeLegalMentions(data.legalMentions));
    });
  }, [isAdmin]);

  const updateSection = (index, patch) => {
    setLegal((current) => ({
      ...current,
      sections: current.sections.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, ...patch } : section
      ),
    }));
  };

  const updateDetail = (sectionIndex, detailIndex, key, value) => {
    setLegal((current) => {
      const sections = current.sections.map((section, index) => {
        if (index !== sectionIndex) return section;
        const details = [...(section.details || [])];
        details[detailIndex] = [
          key === "label" ? value : details[detailIndex]?.[0] || "",
          key === "value" ? value : details[detailIndex]?.[1] || "",
        ];
        return { ...section, details };
      });
      return { ...current, sections };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(
        doc(db, "admin", "content"),
        sanitizeForFirestore({
          legalMentions: legal,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      toast.success("Mentions légales sauvegardées");
    } catch (err) {
      toast.error("Sauvegarde impossible", err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setLegal(mergeLegalMentions(DEFAULT_LEGAL_MENTIONS));
    toast.success("Texte par défaut rechargé", "Clique sur sauvegarder pour publier.");
  };

  if (loading) return <main className="min-h-screen bg-black p-8 text-white">Chargement...</main>;

  if (!user || !isAdmin) {
    return (
      <main className="grid min-h-screen place-items-center bg-black px-6 text-white">
        <section className="max-w-md rounded-lg border border-white/10 bg-zinc-950 p-6 text-center">
          <h1 className="text-3xl font-black uppercase">Admin Mentions légales</h1>
          <p className="mt-3 text-gray-400">Connexion admin requise.</p>
          {user && !isAdmin ? (
            <button onClick={logout} className="mt-6 rounded-full bg-red-600 px-5 py-3 text-sm font-bold uppercase">
              Changer de compte
            </button>
          ) : (
            <button onClick={loginWithGoogle} className="mt-6 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black">
              Connexion Google
            </button>
          )}
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black p-4 text-white md:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-col justify-between gap-4 rounded-lg border border-white/10 bg-zinc-950 p-5 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500">Admin</p>
            <h1 className="mt-2 text-4xl font-black uppercase">Mentions légales</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-400">
              Modifie le contenu de la page /mentions-legales. Les paragraphes sont séparés par une ligne vide.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={resetDefaults} className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              Recharger défaut
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold uppercase text-black disabled:opacity-60"
            >
              <Save size={16} /> {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
            <Link to="/mentions-legales" className="rounded-full border border-white/15 px-4 py-2 text-sm font-bold uppercase">
              Voir la page
            </Link>
          </div>
        </header>

        <section className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Titre page" value={legal.title} onChange={(value) => setLegal((current) => ({ ...current, title: value }))} />
            <Field label="Dernière mise à jour" value={legal.updatedAt} onChange={(value) => setLegal((current) => ({ ...current, updatedAt: value }))} />
          </div>
        </section>

        {legal.sections.map((section, index) => (
          <section key={`${section.title}-${index}`} className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
            <div className="mb-5">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-500">
                Section {String(index + 1).padStart(2, "0")}
              </p>
              <div className="mt-3">
                <Field label="Titre" value={section.title} onChange={(value) => updateSection(index, { title: value })} />
              </div>
            </div>

            <div className="space-y-4">
              <Field
                textarea
                label="Paragraphes"
                value={(section.content || []).join("\n\n")}
                onChange={(value) => updateSection(index, { content: splitBlocks(value) })}
              />

              {section.details && (
                <div className="rounded-lg border border-white/10 bg-black p-4">
                  <p className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-gray-400">
                    Informations détaillées
                  </p>
                  <div className="space-y-3">
                    {section.details.map(([label, value], detailIndex) => (
                      <div key={`${label}-${detailIndex}`} className="grid grid-cols-1 gap-3 md:grid-cols-[0.4fr_0.6fr]">
                        <Field label="Libellé" value={label} onChange={(next) => updateDetail(index, detailIndex, "label", next)} />
                        <Field label="Valeur" value={value} onChange={(next) => updateDetail(index, detailIndex, "value", next)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {section.list && (
                <Field
                  textarea
                  label="Liste"
                  value={(section.list || []).join("\n")}
                  onChange={(value) => updateSection(index, { list: value.split("\n").map((item) => item.trim()).filter(Boolean) })}
                />
              )}

              {section.afterList && (
                <Field
                  textarea
                  label="Paragraphes après liste"
                  value={(section.afterList || []).join("\n\n")}
                  onChange={(value) => updateSection(index, { afterList: splitBlocks(value) })}
                />
              )}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}
